import { HttpClient } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Subscription } from 'rxjs';
import { DialogComponent } from 'src/app/components/dialog/dialog.component';
import { ListaDadosColuna } from 'src/app/components/lista-dados/lista-dados.component';
import { BePontoEletronicoFeriado } from 'src/app/models/BePontoEletronicoFeriado';
import { DialogData } from 'src/app/models/dialog-data';
import { SnackBarService } from 'src/app/services/snack-bar.service';
import { SubService } from 'src/app/services/sub.service';

@Component({
  selector: 'app-ponto-eletronico-feriados',
  templateUrl: './ponto-eletronico-feriados.component.html',
  styleUrls: ['./ponto-eletronico-feriados.component.css']
})
export class PontoEletronicoFeriadosComponent implements OnInit, OnDestroy {
  public feriados: BePontoEletronicoFeriado[] = [];
  public subscription?: Subscription;

  public colunasListaDados: ListaDadosColuna[] = [
    {
      campo: 'dtFeriado',
      rotulo: 'Data',
      tamanho: '85px',
      editavel: true,
      edicao: {
        tipoEditor: 'data',
        validacao: {
          obrigatorio: true
        }
      }
    },
    {
      campo: 'noFeriado',
      rotulo: 'Feriado',
      tamanho: 'calc(100% - 85px)',
      editavel: true,
      edicao: {
        tipoEditor: 'texto',
        validacao: {
          obrigatorio: true,
          minLength: 3,
          maxLength: 100
        }
      }
    }
  ];

  public constructor(
    private httpClient: HttpClient,
    private sub: SubService,
    private dialog: MatDialog,
    private snackBarService: SnackBarService,
  ) {}

  public ngOnInit(): void {
    this.carregarFeriados();
  }

  public ngOnDestroy(): void {
    this.unsubscribe();
  }

  public onSalvarLinha(feriado: BePontoEletronicoFeriado): void {
    this.unsubscribe();

    const payload: BePontoEletronicoFeriado = {
      id: feriado.id ?? null!,
      dtFeriado: this.formatarDataPayload(feriado.dtFeriado),
      noFeriado: (feriado.noFeriado ?? '').trim(),
    };

    const requisicao = payload.id
      ? this.httpClient.put<BePontoEletronicoFeriado>('/api/PontoEletronicoFeriados', payload)
      : this.httpClient.post<BePontoEletronicoFeriado>('/api/PontoEletronicoFeriados', payload);

    this.subscription = requisicao.subscribe({
      next: (res) => {
        this.unsubscribe();
        feriado.id = res.id;
        feriado.dtFeriado = this.criarDataLocal(res.dtFeriado);
        feriado.noFeriado = res.noFeriado;
        this.feriados = [...this.feriados].sort((a, b) => this.compararDatasDesc(a.dtFeriado, b.dtFeriado));
        this.snackBarService.exibirSucesso(payload.id ? 'Registro salvo!' : 'Registro criado!');
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  public onDelete(feriado: BePontoEletronicoFeriado): void {
    this.showDialog('Deseja excluir esse feriado?', 'Alerta', 'Não', 'Sim', feriado);
  }

  public showDialog(
    msg: string,
    statusMsg: string,
    button: string = 'OK',
    secondButton: string = '',
    feriado: BePontoEletronicoFeriado = {} as BePontoEletronicoFeriado
  ): void {
    if (this.subscription)
      return;

    const dialogData: DialogData = {
      title: statusMsg,
      message: msg,
      buttonText: button,
      secondButtonText: secondButton,
    };

    const dialogRef = this.dialog.open(DialogComponent, { data: dialogData });
    dialogRef.afterClosed().subscribe((result) => {
      this.unsubscribe();
      if (result) {
        this.sub.reset(
          this.httpClient.delete(`/api/PontoEletronicoFeriados/${feriado.id}`),
          () => this.onDeleteConfirm(feriado),
          () => this.showDialog('Não foi possível excluir o registro!', 'Erro')
        );
      }
    });
  }

  private carregarFeriados(): void {
    this.unsubscribe();
    this.subscription = this.httpClient.get<BePontoEletronicoFeriado[]>('/api/PontoEletronicoFeriados').subscribe({
      next: (res) => {
        this.feriados = (res ?? []).map(feriado => ({
          ...feriado,
          dtFeriado: this.criarDataLocal(feriado.dtFeriado),
        }));
        this.unsubscribe();
      },
      error: (err) => {
        this.unsubscribe();
        this.snackBarService.exibirErro(`Erro! - ${err.status} - ${err.statusText}`);
      }
    });
  }

  private onDeleteConfirm(feriado: BePontoEletronicoFeriado): void {
    this.feriados = this.feriados.filter(item => item.id !== feriado.id);
    this.snackBarService.exibirSucesso('Registro excluído!');
  }

  private formatarDataPayload(data: Date | string | any): string {
    const dataLocal = this.criarDataLocal(data);
    const ano = dataLocal.getFullYear();
    const mes = `${dataLocal.getMonth() + 1}`.padStart(2, '0');
    const dia = `${dataLocal.getDate()}`.padStart(2, '0');

    return `${ano}-${mes}-${dia}`;
  }

  private criarDataLocal(data: Date | string | any): Date {
    if (data instanceof Date) {
      return data;
    }

    if (data?.toDate instanceof Function) {
      return data.toDate();
    }

    const dataTexto = `${data ?? ''}`;
    const [ano, mes, dia] = dataTexto.substring(0, 10).split('-').map(Number);
    return new Date(ano, mes - 1, dia);
  }

  private compararDatasDesc(a: Date | string | any, b: Date | string | any): number {
    return this.criarDataLocal(b).getTime() - this.criarDataLocal(a).getTime();
  }

  private unsubscribe(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = undefined;
    }
  }
}
